import React, { useMemo } from "react";
import { Table, flexRender } from "@tanstack/react-table";

type Props<T> = {
  table: Table<T>;
  /** column id to show as the blue id/link (top-left) */
  idColId?: string;
  /** column id to show as bold title under id */
  titleColId?: string;
  /** column id that contains action buttons (top-right on mobile cards) */
  actionColId?: string; // default: "actions"
  /** columns to omit from stacked list on mobile (besides id/title/actions) */
  omitMobile?: string[];
  /** min width for desktop table (forces horizontal scroll) */
  tableMinWidth?: number | string;
  /**
   * If provided, whole row/card becomes clickable and will call this callback with
   * the original row data.
   */
  onRowClick?: (row: T) => void;
};

/* ---------- DESKTOP <thead>/<tbody> ONLY (for embedding into your own <table>) ---------- */
export function DesktopTableParts<T>({
  table,
  onRowClick,
}: {
  table: Table<T>;
  onRowClick?: (row: T) => void;
}) {
  return (
    <>
      <thead className="production-thead">
        {table.getHeaderGroups().map((hg) => (
          <tr key={hg.id}>
            {hg.headers.map((h) => (
              <th key={h.id} className="production-th">
                {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr
            key={row.id}
            className="production-tr table-row-hover"
            style={{ cursor: onRowClick ? "pointer" : undefined }}
            onClick={(e) => {
              if (!onRowClick) return;
              const target = e.target as HTMLElement | null;
              // Do not trigger row navigation when user clicks interactive elements inside the row.
              if (target?.closest("a, button, input, select, textarea, label")) return;
              onRowClick(row.original);
            }}
          >
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id} className="production-td">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </>
  );
}

/* ---------- MOBILE CARDS ONLY (stacked label->value, actions top-right) ---------- */
export function MobileCardsOnly<T>({
  table,
  idColId,
  titleColId,
  actionColId = "actions",
  omitMobile = [],
  onRowClick,
}: Omit<Props<T>, "tableMinWidth"> & { table: Table<T> }) {
  const headerById = useMemo(() => {
    const m = new Map<string, any>();
    table.getHeaderGroups().forEach((hg) => hg.headers.forEach((h) => m.set(h.column.id, h)));
    return m;
  }, [table]);

  const rows = table.getRowModel().rows;
  if (!rows.length) return null;

  return (
    <div className="d-flex flex-column gap-3">
      {rows.map((row) => {
        const cells = row.getVisibleCells();
        const idCell = (idColId && cells.find((c) => c.column.id === idColId)) || cells[0];
        const titleCell =
          (titleColId && cells.find((c) => c.column.id === titleColId)) || cells[1] || undefined;
        const actionCell = cells.find((c) => c.column.id === actionColId);

        const listCells = cells.filter(
          (c) =>
            c.column.id !== idCell?.column.id &&
            (!titleCell || c.column.id !== titleCell.column.id) &&
            c.column.id !== actionColId &&
            !omitMobile.includes(c.column.id)
        );

        return (
          <div
            key={row.id}
            className="po-card"
            style={{ cursor: onRowClick ? "pointer" : undefined }}
            onClick={(e) => {
              if (!onRowClick) return;
              const target = e.target as HTMLElement | null;
              if (target?.closest("a, button, input, select, textarea, label")) return;
              onRowClick(row.original);
            }}
          >
            <div className="d-flex justify-content-between align-items-start">
              <div className="pe-2">
                {idCell && (
                  <div className="order-link d-inline-block mb-1">
                    {flexRender(idCell.column.columnDef.cell, idCell.getContext())}
                  </div>
                )}
                {titleCell && (
                  <div className="fw-semibold lh-sm text-body">
                    {flexRender(titleCell.column.columnDef.cell, titleCell.getContext())}
                  </div>
                )}
              </div>

              <div className="po-card-actions d-flex gap-2 flex-wrap">
                {actionCell &&
                  flexRender(actionCell.column.columnDef.cell, actionCell.getContext())}
              </div>
            </div>

            <div className="mt-3">
              {listCells.map((cell) => {
                const header = headerById.get(cell.column.id);
                return (
                  <div key={cell.id} className="po-field">
                    <div className="po-field-label">
                      {header
                        ? flexRender(header.column.columnDef.header, header.getContext())
                        : cell.column.id}
                    </div>
                    <div className="po-field-value">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- ONE COMPONENT that does BOTH RESPONSIVELY ---------- */
export default function ResponsiveDataTable<T>({
  table,
  idColId,
  titleColId,
  actionColId = "actions",
  omitMobile = [],
  tableMinWidth = 1500,
}: Props<T>) {
  const DesktopTable = (
    <div className="production-scroll">
      <table
        className="table production-table align-middle mb-0 "
        style={{
          minWidth: typeof tableMinWidth === "number" ? `${tableMinWidth}px` : tableMinWidth,
        }}
      >
        <DesktopTableParts table={table} />
      </table>
    </div>
  );

  const MobileCards = (
    <MobileCardsOnly
      table={table}
      idColId={idColId}
      titleColId={titleColId}
      actionColId={actionColId}
      omitMobile={omitMobile}
    />
  );

  return (
    <>
      return (
      <>
        {/* show DESKTOP at ≥1022px */}
        <div className="rdt-desktop">{DesktopTable}</div>

        {/* show MOBILE at ≤1021px */}
        <div className="rdt-mobile">{MobileCards}</div>
      </>
      );

    </>
  );
}
